import { useCallback, useEffect, useState } from "react";
import { styled } from "styled-components";

import { InfinityLoader, ListItemBox, LoadingCards } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { DataItem } from "domains/knowledgeData/components/DataItem";
import { StyledTitle } from "domains/knowledgeFeed/components/shared";
import { fetchTopicPinnedKnowledge } from "domains/pinAddReject/actions";
import {
  getIsPinnedKnowledgePending,
  getPinnedKnowledge,
  getPinnedKnowledgeCurrentTopicId,
  getPinnedKnowledgeHasNextPage,
  getPinnedKnowledgeTotalCount,
} from "domains/pinAddReject/selectors";
import { ExpandableKnowledgeItem } from "domains/publication/components/ExpandableKnowledgeItem";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { SmallVideoItem } from "domains/videos/components/SmallVideoItem";
import type { Knowledge } from "entities/knowledge/types";
import { KnowledgeType } from "entities/knowledge/types";
import { getCanSeeSectionContent } from "entities/permissions/selectors";
import { createHasUnauthenticatedContentAccessSelector } from "entities/topics/selectors";

const StyledDataItem = styled(DataItem)`
  ${StyledTitle} {
    font-size: 1rem;
    font-weight: 500;
  }
`;

const SECTION_ID = "pinned-publications";
const NON_EXPANDED_ITEM_LIMIT = 3;
const EXPANDED_ITEM_LIMIT = 20;

type PinnedKnowledgeSectionProps = {
  topicId: string;
  selectedSectionId?: string;
  selectSectionHandle: (id: string) => void;
};

export const PinnedKnowledgeSection = ({
  topicId,
  selectedSectionId,
  selectSectionHandle,
}: PinnedKnowledgeSectionProps) => {
  const isExpanded = SECTION_ID === selectedSectionId;
  const [page, setPage] = useState(1);
  const translate = useTranslate();

  const dispatch = useDispatch();
  const hasUnauthenticatedContentAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const pinnedKnowledge = useSelector(getPinnedKnowledge);
  const isPending = useSelector(getIsPinnedKnowledgePending);
  const canSeeSection = useSelector(getCanSeeSectionContent);
  const hasNextPage = useSelector(getPinnedKnowledgeHasNextPage);
  const totalCount = useSelector(getPinnedKnowledgeTotalCount);
  const storedTopicId = useSelector(getPinnedKnowledgeCurrentTopicId);

  useEffect(() => {
    if (storedTopicId === topicId && !hasNextPage) return;

    dispatch(fetchTopicPinnedKnowledge({ topicId, limit: EXPANDED_ITEM_LIMIT, page, forceRefresh: true }));
  }, [dispatch, hasNextPage, page, storedTopicId, topicId]);

  const isHidden = selectedSectionId !== SECTION_ID && !!selectedSectionId;
  const isLocked = !hasUnauthenticatedContentAccess && /* hasAccess */ !canSeeSection;

  const onSelection = useCallback(() => selectSectionHandle(SECTION_ID), [selectSectionHandle]);

  const loadNextPage = () => {
    setPage((prev) => prev + 1);
  };

  const renderKnowledgeItemByType = (knowledge: Knowledge) => {
    const getPathByKnowledgeType = (knowledgeType: string) => `${location.pathname}/${knowledgeType}/${knowledge.id}`;

    switch (knowledge.knowledgeType) {
      case KnowledgeType.video:
        return <SmallVideoItem key={knowledge.id} knowledge={knowledge} link={getPathByKnowledgeType("videos")} />;
      case KnowledgeType.publication:
        return <ExpandableKnowledgeItem knowledge={knowledge} link={getPathByKnowledgeType("publications")} />;
      case KnowledgeType.data:
        return <StyledDataItem knowledge={knowledge} link={getPathByKnowledgeType("interactive-data")} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (pinnedKnowledge.length !== 0 || !isExpanded) return;

    onSelection();
  }, [pinnedKnowledge.length, isExpanded, onSelection]);

  if (pinnedKnowledge.length === 0 && !isPending) {
    return null;
  }

  return (
    <TopicDetailSection
      title={translate("app.pinned_knowledge.title")}
      display
      topicId={topicId}
      selectSectionHandle={onSelection}
      isHidden={isHidden}
      isExpanded={isExpanded}
      isPending={isPending}
      isLocked={isLocked}
      hasNextPage={totalCount > NON_EXPANDED_ITEM_LIMIT}
      iconVariant="pin"
    >
      <InfinityLoader loadNextPage={loadNextPage} hasNextPage={isExpanded && hasNextPage} isPending={isPending}>
        {pinnedKnowledge.slice(0, isExpanded ? undefined : NON_EXPANDED_ITEM_LIMIT).map((knowledge) => (
          <ListItemBox key={knowledge.id}>{renderKnowledgeItemByType(knowledge)}</ListItemBox>
        ))}
        {isPending && pinnedKnowledge.length === 0 && !isHidden && (
          <LoadingCards
            cardHeight={66}
            displayDetailButton
            imageHeight={50}
            imageWidth={50}
            lines={[30, 100]}
            numberOfItems={NON_EXPANDED_ITEM_LIMIT}
          />
        )}
      </InfinityLoader>
    </TopicDetailSection>
  );
};
